import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  TrendingDown, TrendingUp, AlertTriangle, CheckCircle, 
  DollarSign, Users, Calendar, RefreshCcw, Loader2, Target,
  Eye, UserCheck, MessageSquare, UserPlus, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../utils/apiUrl';

const ShortfallOverviewPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [shortfallData, setShortfallData] = useState([]);
  const [periodType, setPeriodType] = useState('daily');
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchShortfalls = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/target-definition/shortfall-overview?period_type=${periodType}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setShortfallData(data);
      }
    } catch (err) {
      console.error('Error fetching shortfalls:', err);
      toast.error('Failed to load shortfall data');
    } finally {
      setLoading(false);
    }
  }, [token, periodType]);

  useEffect(() => {
    fetchShortfalls();
  }, [fetchShortfalls]);

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '₹0';
    return `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  };

  const getStatusBadge = (percent) => {
    if (percent >= 100) return <Badge className="bg-emerald-600"><CheckCircle className="w-3 h-3 mr-1" />Done</Badge>;
    if (percent >= 75) return <Badge className="bg-amber-600">{percent}%</Badge>;
    return <Badge className="bg-red-600"><AlertTriangle className="w-3 h-3 mr-1" />{percent}%</Badge>;
  };

  const getProgressBar = (percent) => {
    const color = percent >= 100 ? 'bg-emerald-500' : percent >= 75 ? 'bg-amber-500' : 'bg-red-500';
    return (
      <div className="w-16 bg-slate-700 rounded-full h-1.5">
        <div className={`h-1.5 rounded-full ${color}`} style={{ width: `${Math.min(percent || 0, 100)}%` }} />
      </div>
    );
  };

  // Calculate summary stats
  const totalInShortfall = shortfallData.filter(s => s.overall_status === 'red').length;
  const totalOnTrack = shortfallData.filter(s => s.overall_status === 'green').length;
  const totalBrokerageShortfall = shortfallData.reduce((sum, s) => sum + (s.shortfalls?.brokerage?.shortfall || 0), 0);
  const totalTradedShortfall = shortfallData.reduce((sum, s) => sum + (s.shortfalls?.traded_clients?.shortfall || 0), 0);
  const totalActivationShortfall = shortfallData.reduce((sum, s) => sum + (s.shortfalls?.client_activation?.shortfall || 0), 0);
  const totalNewClientsShortfall = shortfallData.reduce((sum, s) => sum + (s.shortfalls?.new_clients?.shortfall || 0), 0);
  const totalMeetingsShortfall = shortfallData.reduce((sum, s) => sum + (s.shortfalls?.meetings?.shortfall || 0), 0);

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setShowDetailDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="shortfall-overview-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Shortfall Overview</h1>
          <p className="text-slate-400">Track target shortfalls across all RMs and Dealers</p>
        </div>
        <div className="flex gap-2">
          <Select value={periodType} onValueChange={setPeriodType}>
            <SelectTrigger className="w-40 bg-slate-800 border-white/10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Last Trading Day</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchShortfalls} className="border-[#C5A059]/30">
            <RefreshCcw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Card className="card-dark border-white/5">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-slate-500" />
              <div>
                <p className="text-xs text-slate-400">Total</p>
                <p className="text-lg font-bold text-white">{shortfallData.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dark border-red-500/30 bg-red-500/5">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-red-500" />
              <div>
                <p className="text-xs text-red-400">In Shortfall</p>
                <p className="text-lg font-bold text-red-400">{totalInShortfall}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dark border-emerald-500/30 bg-emerald-500/5">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              <div>
                <p className="text-xs text-emerald-400">On Track</p>
                <p className="text-lg font-bold text-emerald-400">{totalOnTrack}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dark border-amber-500/30 bg-amber-500/5">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-amber-500" />
              <div>
                <p className="text-xs text-amber-400">Brokerage Gap</p>
                <p className="text-lg font-bold text-amber-400">{formatCurrency(totalBrokerageShortfall)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dark border-blue-500/30 bg-blue-500/5">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-xs text-blue-400">Traded Gap</p>
                <p className="text-lg font-bold text-blue-400">{totalTradedShortfall}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dark border-purple-500/30 bg-purple-500/5">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-purple-500" />
              <div>
                <p className="text-xs text-purple-400">Activation Gap</p>
                <p className="text-lg font-bold text-purple-400">{totalActivationShortfall}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {periodType === 'monthly' && (
          <Card className="card-dark border-cyan-500/30 bg-cyan-500/5">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-cyan-500" />
                <div>
                  <p className="text-xs text-cyan-400">Meetings Gap</p>
                  <p className="text-lg font-bold text-cyan-400">{totalMeetingsShortfall}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="card-dark border-green-500/30 bg-green-500/5">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-green-500" />
              <div>
                <p className="text-xs text-green-400">New Clients Gap</p>
                <p className="text-lg font-bold text-green-400">{totalNewClientsShortfall}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dark border-cyan-500/30 bg-cyan-500/5">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-cyan-500" />
              <div>
                <p className="text-xs text-cyan-400">Meetings Gap</p>
                <p className="text-lg font-bold text-cyan-400">{totalMeetingsShortfall}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Shortfall Table */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList className="bg-slate-800">
          <TabsTrigger value="all" className="data-[state=active]:bg-[#C5A059]">All</TabsTrigger>
          <TabsTrigger value="shortfall" className="data-[state=active]:bg-red-600">In Shortfall</TabsTrigger>
          <TabsTrigger value="on-track" className="data-[state=active]:bg-emerald-600">On Track</TabsTrigger>
        </TabsList>

        {['all', 'shortfall', 'on-track'].map((tab) => (
          <TabsContent key={tab} value={tab}>
            <Card className="card-dark border-white/5">
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-slate-400 sticky left-0 bg-slate-900">Name</TableHead>
                      <TableHead className="text-slate-400">Role</TableHead>
                      <TableHead className="text-slate-400 text-center">Traded Clients</TableHead>
                      <TableHead className="text-slate-400 text-center">Brokerage</TableHead>
                      <TableHead className="text-slate-400 text-center">Client Activation</TableHead>
                      <TableHead className="text-slate-400 text-center">Tier Trade</TableHead>
                      <TableHead className="text-slate-400 text-center">New Clients</TableHead>
                      <TableHead className="text-slate-400 text-center">Meetings</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-right text-slate-400">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {shortfallData
                      .filter(s => {
                        if (tab === 'shortfall') return s.overall_status === 'red';
                        if (tab === 'on-track') return s.overall_status === 'green';
                        return true;
                      })
                      .map((item) => {
                        const traded = item.shortfalls?.traded_clients || {};
                        const brokerage = item.shortfalls?.brokerage || {};
                        const activation = item.shortfalls?.client_activation || {};
                        const tierTrade = item.shortfalls?.tier_trade || {};
                        const newClients = item.shortfalls?.new_clients || {};
                        const meetings = item.shortfalls?.meetings || {};

                        return (
                          <TableRow key={item.user_id} className="border-white/5 hover:bg-white/5">
                            <TableCell className="text-white font-medium sticky left-0 bg-slate-900">{item.user_name}</TableCell>
                            <TableCell>
                              <Badge className={item.role === 'rm' ? 'bg-blue-600' : 'bg-purple-600'}>
                                {item.role?.toUpperCase()}
                              </Badge>
                            </TableCell>
                            {/* Traded Clients */}
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center gap-1">
                                <span className="text-white text-sm">{traded.achieved || 0}/{traded.target || 0}</span>
                                {getProgressBar(traded.percent_achieved)}
                                {traded.shortfall > 0 && (
                                  <span className="text-red-400 text-xs">-{traded.shortfall}</span>
                                )}
                              </div>
                            </TableCell>
                            {/* Brokerage */}
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center gap-1">
                                <span className="text-white text-sm">{formatCurrency(brokerage.achieved)}</span>
                                <span className="text-slate-500 text-xs">/ {formatCurrency(brokerage.target)}</span>
                                {getProgressBar(brokerage.percent_achieved)}
                                {brokerage.shortfall > 0 && (
                                  <span className="text-red-400 text-xs">-{formatCurrency(brokerage.shortfall)}</span>
                                )}
                              </div>
                            </TableCell>
                            {/* Client Activation */}
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center gap-1">
                                <span className="text-white text-sm">{activation.achieved || 0}/{activation.target || 0}</span>
                                {getProgressBar(activation.percent_achieved)}
                                {activation.shortfall > 0 && (
                                  <span className="text-red-400 text-xs">-{activation.shortfall}</span>
                                )}
                              </div>
                            </TableCell>
                            {/* Tier Trade - All periods */}
                              <TableCell className="text-center">
                                <div className="flex gap-1 justify-center text-xs flex-wrap">
                                  <span className="px-1 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">P:{tierTrade.platinum?.achieved || 0}/{tierTrade.platinum?.target || 0}</span>
                                  <span className="px-1 py-0.5 rounded bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">G:{tierTrade.gold?.achieved || 0}/{tierTrade.gold?.target || 0}</span>
                                  <span className="px-1 py-0.5 rounded bg-slate-500/10 text-gray-300 border border-slate-500/20">S:{tierTrade.silver?.achieved || 0}/{tierTrade.silver?.target || 0}</span>
                                  <span className="px-1 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">B:{tierTrade.blue?.achieved || 0}/{tierTrade.blue?.target || 0}</span>
                                </div>
                              </TableCell>
                            {/* New Clients - Always shown */}
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center gap-1">
                                <span className="text-white text-sm">{newClients.achieved || 0}/{newClients.target || 0}</span>
                                {getProgressBar(newClients.percent_achieved)}
                                {newClients.shortfall > 0 && (
                                  <span className="text-red-400 text-xs">-{newClients.shortfall}</span>
                                )}
                              </div>
                            </TableCell>
                            {/* Meetings - Always shown */}
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center gap-1">
                                <span className="text-white text-sm">{meetings.achieved || 0}/{meetings.target || 0}</span>
                                {getProgressBar(meetings.percent_achieved)}
                                {meetings.shortfall > 0 && (
                                  <span className="text-red-400 text-xs">-{meetings.shortfall}</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {item.overall_status === 'green' ? (
                                <Badge className="bg-emerald-600">
                                  <CheckCircle className="w-3 h-3 mr-1" /> On Track
                                </Badge>
                              ) : (
                                <Badge className="bg-red-600">
                                  <AlertTriangle className="w-3 h-3 mr-1" /> Shortfall
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleViewDetails(item)}
                                className="hover:bg-white/10"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {shortfallData.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-8 text-slate-400">
                          No data available. Generate targets first.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-[#C5A059]" />
              {selectedUser?.user_name} - {periodType === 'daily' ? 'Last Trading Day' : periodType.charAt(0).toUpperCase() + periodType.slice(1)} Shortfall Details
            </DialogTitle>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-4">
              {/* Traded Clients */}
              <div className="bg-slate-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-400 flex items-center gap-2">
                    <Users className="w-4 h-4" /> Traded Clients
                  </span>
                  {getStatusBadge(selectedUser.shortfalls?.traded_clients?.percent_achieved || 0)}
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Target</p>
                    <p className="text-white font-bold">{selectedUser.shortfalls?.traded_clients?.target || 0}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Achieved</p>
                    <p className="text-white font-bold">{selectedUser.shortfalls?.traded_clients?.achieved || 0}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Shortfall</p>
                    <p className="text-red-400 font-bold">{selectedUser.shortfalls?.traded_clients?.shortfall || 0}</p>
                  </div>
                </div>
              </div>

              {/* Brokerage */}
              <div className="bg-slate-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-400 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" /> Brokerage
                  </span>
                  {getStatusBadge(selectedUser.shortfalls?.brokerage?.percent_achieved || 0)}
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Target</p>
                    <p className="text-white font-bold">{formatCurrency(selectedUser.shortfalls?.brokerage?.target)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Achieved</p>
                    <p className="text-white font-bold">{formatCurrency(selectedUser.shortfalls?.brokerage?.achieved)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Shortfall</p>
                    <p className="text-red-400 font-bold">{formatCurrency(selectedUser.shortfalls?.brokerage?.shortfall)}</p>
                  </div>
                </div>
              </div>

              {/* Client Activation */}
              <div className="bg-slate-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-400 flex items-center gap-2">
                    <UserCheck className="w-4 h-4" /> Client Activation
                  </span>
                  {getStatusBadge(selectedUser.shortfalls?.client_activation?.percent_achieved || 0)}
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Target</p>
                    <p className="text-white font-bold">{selectedUser.shortfalls?.client_activation?.target || 0}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Achieved</p>
                    <p className="text-white font-bold">{selectedUser.shortfalls?.client_activation?.achieved || 0}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Shortfall</p>
                    <p className="text-red-400 font-bold">{selectedUser.shortfalls?.client_activation?.shortfall || 0}</p>
                  </div>
                </div>
              </div>

              {/* Tier Trade (All periods) */}
              {selectedUser.shortfalls?.tier_trade && (
                <div className="bg-slate-800 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-slate-400 flex items-center gap-2">
                      <Layers className="w-4 h-4" /> Tier Trade Targets
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-3 text-sm">
                    {['platinum', 'gold', 'silver', 'blue'].map((tier) => {
                      const data = selectedUser.shortfalls?.tier_trade?.[tier] || {};
                      const colorConfig = {
                        platinum: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', text: 'text-cyan-400' },
                        gold: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/20', text: 'text-yellow-400' },
                        silver: { bg: 'bg-slate-500/10', border: 'border-slate-500/20', text: 'text-gray-300' },
                        blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-400' }
                      };
                      const c = colorConfig[tier];
                      return (
                        <div key={tier} className={`text-center p-2 rounded-lg ${c.bg} border ${c.border}`}>
                          <p className={`${c.text} font-medium capitalize text-xs mb-1`}>{tier}</p>
                          <p className="text-white font-bold">{data.achieved || 0}/{data.target || 0}</p>
                          {data.shortfall > 0 && <p className="text-red-400 text-xs mt-0.5">-{data.shortfall}</p>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* New Clients & Meetings (Monthly) */}
              {periodType === 'monthly' && (
                <>
                  <div className="bg-slate-800 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-slate-400 flex items-center gap-2">
                        <UserPlus className="w-4 h-4" /> New Clients
                      </span>
                      {getStatusBadge(selectedUser.shortfalls?.new_clients?.percent_achieved || 0)}
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Target</p>
                        <p className="text-white font-bold">{selectedUser.shortfalls?.new_clients?.target || 0}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Achieved</p>
                        <p className="text-white font-bold">{selectedUser.shortfalls?.new_clients?.achieved || 0}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Shortfall</p>
                        <p className="text-red-400 font-bold">{selectedUser.shortfalls?.new_clients?.shortfall || 0}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-800 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-slate-400 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" /> Meetings
                      </span>
                      {getStatusBadge(selectedUser.shortfalls?.meetings?.percent_achieved || 0)}
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Target</p>
                        <p className="text-white font-bold">{selectedUser.shortfalls?.meetings?.target || 0}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Achieved</p>
                        <p className="text-white font-bold">{selectedUser.shortfalls?.meetings?.achieved || 0}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Shortfall</p>
                        <p className="text-red-400 font-bold">{selectedUser.shortfalls?.meetings?.shortfall || 0}</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShortfallOverviewPage;
